/**
 * Generic sort-comparator factories. Pure-leaf module: its only value import is the sibling const leaf holding the
 * direction vocabulary, so it still imports NO utils sibling.
 * @runtime-derived-ssot-helper (const-leaf-pinned): consumed from the utils/ layer, which sits at/below the utils floor and cannot import a utils sibling.
 *
 * `const/sort-direction.ts` holds BOTH direction vocabularies: `SQL_SORT_DIRECTION` (UPPERCASE `ASC`/`DESC`, for raw
 * SQL ORDER BY clauses) and `SORT_DIRECTION` (lowercase, the URL/state spelling the `SortDirection` union is derived
 * from). This module consumes the LOWERCASE one. The two vocabularies are NOT substitutable -- do not cross-wire them.
 */

import { SORT_DIRECTION } from '@federa/const/sort-direction';
import type { SortDirection } from '@federa/types/primitives-status';

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic comparator factory homed in const/ so pure utils/ leaves
 * can value-import it without a forbidden cross-utils sibling dependency. Returns an `Array.prototype.sort`
 * comparator ordering items by the numeric value `accessor` extracts.
 *
 * SUBTRACTION-based, not comparison-based, so it is only correct for FINITE numbers: `NaN` propagates through the
 * subtraction and yields an inconsistent comparator (sort order becomes implementation-defined), and two infinities
 * of the same sign subtract to `NaN` as well. Callers reading a possibly-absent numeric field must coalesce to a
 * real number in the accessor -- that is the accessor's job, deliberately not this factory's.
 */
export function sortByField<T>(accessor: (item: T) => number, direction: SortDirection): (a: T, b: T) => number {
	const sign = direction === SORT_DIRECTION.DESC ? -1 : 1;
	return (a, b) => sign * (accessor(a) - accessor(b));
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the LOCALE string order as a named comparator, so an id list is
 * sorted `.sort(compareLocaleStrings)` rather than through a re-typed `(a, b) => a.localeCompare(b)` lambda. Locale
 * order folds case on most ICU locales; a caller that needs CODE-UNIT order (a stable on-disk listing, a generated
 * union) must not take this one -- `const/generated-id-union-leaf.ts` records why it spells its own.
 */
export function compareLocaleStrings(a: string, b: string): number {
	return a.localeCompare(b);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the members of a set or list as a FRESH array in CODE-UNIT order --
 * the stable, locale-free order a report, a manifest or a generated listing emits, spelled once rather than as a
 * `[...members].sort()` copy at each caller. The source is never sorted in place.
 */
export function codeUnitSorted(values: Iterable<string>): string[] {
	return [...values].sort();
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): CODE-UNIT string order as a named comparator -- the comparator twin
 * of {@link codeUnitSorted}, locale-free, so ISO-8601 UTC instants of one fixed format (`toISOString`) order exactly as
 * the instants they spell, and the empty string first.
 */
export function compareCodeUnits(a: string, b: string): number {
	if (a === b) return 0;
	return a < b ? -1 : 1;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link compareLocaleStrings} over the KEY of `[key, value]` entry
 * tuples -- the order `Object.entries(record).sort(...)` and a Map's entries take by key, rather than a re-typed
 * `([a], [b]) => a.localeCompare(b)` lambda at each caller.
 */
export function compareEntriesByKey(a: readonly [string, unknown], b: readonly [string, unknown]): number {
	return compareLocaleStrings(a[0], b[0]);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link compareCodeUnits} over the KEY of `[key, value]` entry tuples
 * -- the locale-free twin of {@link compareEntriesByKey}, for a record keyed in the stable order a stored state or a
 * report emits, rather than a re-typed `([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)` lambda at each caller.
 */
export function compareEntriesByCodeUnitKey(a: readonly [string, unknown], b: readonly [string, unknown]): number {
	return compareCodeUnits(a[0], b[0]);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the NUMERIC twin of {@link compareEntriesByKey} -- `[offset, value]`
 * entries smallest key first, the order a Map keyed by source offset or line is read back in, rather than a re-typed
 * `([a], [b]) => a - b` lambda at each rule that collects hits by position.
 */
export function compareEntriesByNumericKey(a: readonly [number, unknown], b: readonly [number, unknown]): number {
	return a[0] - b[0];
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a string-keyed map's entries as a FRESH array in
 * {@link compareEntriesByKey} order, rather than a `[...map.entries()].sort(compareEntriesByKey)` copy at each reader.
 */
export function entriesSortedByKey<V>(map: ReadonlyMap<string, V>): [string, V][] {
	return [...map.entries()].sort(compareEntriesByKey);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the NUMERIC twin of {@link entriesSortedByKey} -- a map keyed by
 * source offset or line read back smallest key first ({@link compareEntriesByNumericKey}), as a FRESH array.
 */
export function entriesSortedByNumericKey<V>(map: ReadonlyMap<number, V>): [number, V][] {
	return [...map.entries()].sort(compareEntriesByNumericKey);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the CODE-UNIT twin of {@link entriesSortedByKey} -- any string-keyed
 * entries (a map, or a list of pairs) as a FRESH array in {@link compareEntriesByCodeUnitKey} order, the stable order a
 * stored state or a report record is keyed in, rather than a `[...entries].sort(compareEntriesByCodeUnitKey)` copy.
 */
export function entriesSortedByCodeUnitKey<E extends readonly [string, unknown]>(entries: Iterable<E>): E[] {
	return [...entries].sort(compareEntriesByCodeUnitKey);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): `[key, count]` entries LARGEST COUNT FIRST -- the ranking a tally
 * report prints its heads in, spelled once rather than as a `(a, b) => b[1] - a[1]` lambda at each census. Ties keep
 * their input order; a report that must not ride the tally's insertion order takes {@link compareEntriesByCountDescThenKey}.
 */
export function compareEntriesByCountDesc(a: readonly [string, number], b: readonly [string, number]): number {
	return b[1] - a[1];
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link compareEntriesByCountDesc} with a count tie broken by the key
 * in locale order ({@link compareEntriesByKey}), so the ranking is a function of the tally alone.
 */
export function compareEntriesByCountDescThenKey(a: readonly [string, number], b: readonly [string, number]): number {
	return compareEntriesByCountDesc(a, b) || compareEntriesByKey(a, b);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): NUMERIC ascending order as a named comparator, so a number list is
 * sorted `.sort(compareNumbersAscending)` rather than through a re-typed `(a, b) => a - b` lambda (a bare `.sort()`
 * orders numbers as TEXT). Subtraction-based, so finite numbers only -- the same limit {@link sortByField} states.
 */
export function compareNumbersAscending(a: number, b: number): number {
	return a - b;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the numbers of a set or list as a FRESH array smallest first -- the
 * numeric twin of {@link codeUnitSorted}, spelled once rather than as a `[...values].sort(compareNumbersAscending)` copy
 * at each census and fold. The source is never sorted in place.
 */
export function numbersAscending(values: Iterable<number>): number[] {
	return [...values].sort(compareNumbersAscending);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): dated entries OLDEST FIRST by their parsed epoch instant (`atMs`),
 * the order every availability and verdict fold walks its episodes in before pairing each with the next.
 */
export function compareAtMsAscending<T extends { readonly atMs: number }>(left: T, right: T): number {
	return compareNumbersAscending(left.atMs, right.atMs);
}
